import { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArchiveRestore, ArrowLeft, Hammer, Loader2, RotateCcw } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { PageHeader } from '@/components/efs/PageHeader';
import { ListPagination } from '@/components/efs/ListPagination';
import { useBatchContext } from '@/app/BatchContext';
import { useCurrentUser } from '@/lib/auth-context';
import { hasPermission } from '@/lib/permissions';
import { LocalizedApiError } from '@/features/audit-issues/LocalizedApiError';

import { DismantlingSessionDialog } from './DismantlingSessionDialog';
import { CancelDismantlingSessionDialog } from './CancelDismantlingSessionDialog';
import { CancelledDismantlingSessionsDialog } from './CancelledDismantlingSessionsDialog';
import {
  useDismantlingPackages,
  useDismantlingPackagesByIds,
  useDismantlingSession,
} from './useDismantling';
import {
  dismantlingRouteValues,
  packageHasNoRecordedOutput,
  type DismantlingOperationalStatus,
  type DismantlingPackageDto,
} from './types';

const NS = 'operations';
const ALL = '__all__';
const DEFAULT_PER_PAGE = 50;
const PAGE_SIZE_OPTIONS = [50, 100];

type ProcessedFilter = 'all' | 'true' | 'false';

const formatKg = (value: number, locale: string): string =>
  new Intl.NumberFormat(locale || 'ja-JP', {
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  }).format(value);

const formatRatio = (p: number | null | undefined): string | null => {
  if (p == null || !Number.isFinite(p)) return null;
  return `${p.toFixed(2)}%`;
};

const progressTone = (
  status: DismantlingOperationalStatus,
): 'default' | 'secondary' | 'outline' => {
  if (status === 'confirmed' || status === 'locked') return 'default';
  if (status === 'draft') return 'outline';
  return 'secondary';
};

export const DismantlingListPage = () => {
  const { t, i18n } = useTranslation(NS);
  const params = useParams<{ batchId?: string }>();
  const { batchId: ctxBatchId } = useBatchContext();
  const batchId = params.batchId ?? ctxBatchId ?? undefined;
  const [searchParams, setSearchParams] = useSearchParams();
  const user = useCurrentUser();

  const canBulkCreate = hasPermission(user, 'dismantling.manage');

  const [processed, setProcessed] = useState<ProcessedFilter>('all');
  const [routeFilter, setRouteFilter] = useState<string>(ALL);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE);
  const [activePkgs, setActivePkgs] = useState<DismantlingPackageDto[] | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  /** Requested pallets the batch could not offer for dismantling. */
  const [droppedCount, setDroppedCount] = useState(0);
  /** The `packages` request already resolved, so re-renders do not re-open it. */
  const [resolvedPackagesKey, setResolvedPackagesKey] = useState<string | null>(null);
  const [cancelledOpen, setCancelledOpen] = useState(false);
  const [cancelSessionId, setCancelSessionId] = useState<string | null>(null);

  // `packages` is the 仕分け hand-off (N ids); `package` is the older
  // single-pallet link still emitted by the re-dismantling dialog.
  const requestedPackageIds = useMemo(() => {
    const raw = searchParams.get('packages') ?? searchParams.get('package');
    if (!raw) return [] as string[];
    // Deduped: `?packages=A,A` would otherwise open pallet A twice, doubling
    // the displayed input weight and duplicating its row key.
    return Array.from(
      new Set(
        raw
          .split(',')
          .map((id) => id.trim())
          .filter(Boolean),
      ),
    );
  }, [searchParams]);
  const requestedPackagesKey = requestedPackageIds.join(',');
  const requestedSessionId = searchParams.get('session');
  const fromSortingSearch = searchParams.get('from');

  const filters = useMemo(
    () => ({
      processed,
      route_type: routeFilter === ALL ? undefined : routeFilter,
      page,
      per_page: perPage,
    }),
    [page, perPage, processed, routeFilter],
  );

  const query = useDismantlingPackages(batchId, filters);
  const deepLinkQuery = useDismantlingPackagesByIds(batchId, requestedPackageIds);
  const cancelSessionQuery = useDismantlingSession(cancelSessionId);
  const rows = useMemo(() => query.data?.data ?? [], [query.data?.data]);
  const total = query.data?.meta?.total ?? 0;

  useEffect(() => {
    if (!query.data || query.isPlaceholderData) return;
    const lastPage = Math.max(1, Math.ceil(total / perPage));
    if (page <= lastPage) return;
    setSelectedIds(new Set());
    setPage(lastPage);
  }, [page, perPage, query.data, query.isPlaceholderData, total]);

  const selectableRows = useMemo(() => rows.filter((row) => !row.dismantling_session_id), [rows]);
  const selectedRows = useMemo(
    () => selectableRows.filter((row) => selectedIds.has(row.inbound_package_id)),
    [selectableRows, selectedIds],
  );
  const selectedKg = useMemo(
    () =>
      selectedRows.reduce((sum, row) => {
        const kg = Number.parseFloat(row.net_kg ?? '');
        return Number.isFinite(kg) ? sum + kg : sum;
      }, 0),
    [selectedRows],
  );
  const allSelectableSelected =
    selectableRows.length > 0 &&
    selectableRows.every((row) => selectedIds.has(row.inbound_package_id));

  useEffect(() => {
    setSelectedIds((prev) => {
      const allowed = new Set(selectableRows.map((row) => row.inbound_package_id));
      const next = new Set(Array.from(prev).filter((id) => allowed.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [selectableRows]);

  // Requested pallets are resolved by the server, not against the list window:
  // the window is one filtered page, so the hand-off would otherwise silently
  // drop pallets it cannot see.
  // Keyed on the REQUESTED ids, not on whether a dialog happens to be open:
  // the session dialog itself links to a derived pallet, so a new request can
  // arrive while the old dialog is still mounted (it is remounted below via
  // its `key`).
  useEffect(() => {
    if (requestedPackagesKey === '') {
      setResolvedPackagesKey(null);
      return;
    }
    if (resolvedPackagesKey === requestedPackagesKey) return;
    const resolved = deepLinkQuery.data?.data;
    if (!resolved) return;
    const byId = new Map(resolved.map((row) => [row.inbound_package_id, row]));
    const ordered = requestedPackageIds
      .map((id) => byId.get(id))
      .filter((row): row is DismantlingPackageDto => !!row);
    setResolvedPackagesKey(requestedPackagesKey);
    setDroppedCount(requestedPackageIds.length - ordered.length);
    setActivePkgs(ordered.length > 0 ? ordered : null);
  }, [deepLinkQuery.data, requestedPackageIds, requestedPackagesKey, resolvedPackagesKey]);

  // A session id is only known to rows in the window; matching it there is
  // enough, and comparing session ids alone can no longer pick up a
  // session-less row through a null === null match.
  useEffect(() => {
    if (activePkgs || !requestedSessionId || rows.length === 0) return;
    const requested = rows.find((row) => row.dismantling_session_id === requestedSessionId);
    if (requested) setActivePkgs([requested]);
  }, [activePkgs, requestedSessionId, rows]);

  const togglePkgSelected = (pkg: DismantlingPackageDto) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(pkg.inbound_package_id)) {
        next.delete(pkg.inbound_package_id);
      } else {
        next.add(pkg.inbound_package_id);
      }
      return next;
    });
  };

  const openMergeDialog = () => {
    if (selectedRows.length === 0) return;
    setActivePkgs(selectedRows);
  };

  const completedCount = useMemo(
    () => rows.filter((r) => r.session_status === 'confirmed').length,
    [rows],
  );
  const completion = total > 0 ? Math.round((completedCount / total) * 100) : 0;

  const routeLabel = (rt: string): string =>
    t(`efs.masters.route_type.${rt}`, { ns: 'masters', defaultValue: rt });

  // `from` carries the 仕分け page's own query string, so going back restores
  // the filters the operator was working in. useSearchParams already decoded it.
  const backToSortingHref = useMemo(() => {
    if (!batchId) return null;
    const base = `/batches/${batchId}/sorting`;
    if (!fromSortingSearch) return base;
    return `${base}${fromSortingSearch.startsWith('?') ? '' : '?'}${fromSortingSearch}`;
  }, [batchId, fromSortingSearch]);

  const operationalStatusLabel = (
    status: DismantlingOperationalStatus | null | undefined,
    sessionStatus: string | null | undefined,
    hasSession: boolean,
    outputCount: number,
    progress: number | null | undefined,
  ): string => {
    const ratioLabel = formatRatio(progress);
    const effectiveStatus =
      status ??
      (!hasSession
        ? 'draft'
        : sessionStatus === 'locked'
          ? 'locked'
          : sessionStatus === 'confirmed'
            ? 'confirmed'
            : outputCount > 0 || progress != null
              ? 'in_progress'
              : 'draft');
    const label = t(`efs.operations.dismantling.operational_statuses.${effectiveStatus}`);
    if (effectiveStatus === 'draft') return label;
    if (ratioLabel == null) {
      return `${label} · ${t('efs.operations.dismantling.progress.ratio_unavailable', {
        defaultValue: '重量比未計算',
      })}`;
    }
    return `${label} · ${ratioLabel}`;
  };

  // Shown for a completed session (with batch-confirm) and for an unfinished
  // one with no output weight, so a pallet started by mistake can be freed.
  const canReturnToSorting = (pkg: DismantlingPackageDto): boolean => {
    if (!pkg.dismantling_session_id || !hasPermission(user, 'dismantling.manage')) return false;
    if (pkg.session_status === 'confirmed') {
      return hasPermission(user, 'processing.dismantling.batch_confirm');
    }
    return pkg.session_status === 'imported' && packageHasNoRecordedOutput(pkg);
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title={t('efs.operations.dismantling.title')}
        subtitle={t('efs.operations.dismantling.subtitle')}
        count={total}
        actions={
          <>
            {backToSortingHref ? (
              <Button asChild variant="outline" size="sm">
                <Link to={backToSortingHref}>
                  <ArrowLeft className="mr-1 h-4 w-4" />
                  {t('efs.operations.dismantling.actions.back_to_sorting')}
                </Link>
              </Button>
            ) : null}
            {canBulkCreate && batchId ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setCancelledOpen(true)}
              >
                <ArchiveRestore className="mr-1 h-4 w-4" />
                {t('efs.operations.dismantling.actions.recently_cancelled', {
                  defaultValue: '最近取消した解体',
                })}
              </Button>
            ) : null}
            <Badge variant={completion === 100 ? 'default' : 'secondary'}>
              {t('efs.operations.dismantling.completion', {
                percent: completion,
                done: completedCount,
                total,
                defaultValue: '完成度 {{percent}}%（{{done}}/{{total}}）',
              })}
            </Badge>
          </>
        }
      />

      {droppedCount > 0 ? (
        <p
          role="status"
          className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-100"
        >
          {t('efs.operations.dismantling.deep_link.dropped', { count: droppedCount })}
        </p>
      ) : null}

      {cancelSessionId && cancelSessionQuery.error ? (
        <div
          role="alert"
          className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-900 dark:border-red-800 dark:bg-red-950 dark:text-red-100"
        >
          <LocalizedApiError
            error={cancelSessionQuery.error}
            showTechnicalDetails={hasPermission(user, 'system.admin')}
          />
        </div>
      ) : null}

      <div className="flex flex-wrap items-center gap-3 rounded-md border border-efs-border bg-efs-bg-panel p-3">
        <div className="flex items-center gap-2">
          <label className="text-xs text-efs-text-muted">
            {t('efs.operations.dismantling.filters.status_label', { defaultValue: '状態' })}
          </label>
          <Select
            value={processed}
            onValueChange={(v) => {
              setPage(1);
              setSelectedIds(new Set());
              setProcessed(v as ProcessedFilter);
            }}
          >
            <SelectTrigger className="h-8 w-32 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">
                {t('efs.operations.dismantling.filters.all', { defaultValue: '全て' })}
              </SelectItem>
              <SelectItem value="false">
                {t('efs.operations.dismantling.filters.pending', {
                  defaultValue: '未処理',
                })}
              </SelectItem>
              <SelectItem value="true">
                {t('efs.operations.dismantling.filters.done', { defaultValue: '完了' })}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-2">
          <label className="text-xs text-efs-text-muted">
            {t('efs.operations.dismantling.filters.route_label', { defaultValue: '処理方式' })}
          </label>
          <Select
            value={routeFilter}
            onValueChange={(value) => {
              setPage(1);
              setSelectedIds(new Set());
              setRouteFilter(value);
            }}
          >
            <SelectTrigger className="h-8 w-44 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>
                {t('efs.operations.dismantling.filters.all_routes', {
                  defaultValue: '全ての方式',
                })}
              </SelectItem>
              {dismantlingRouteValues.map((rt) => (
                <SelectItem key={rt} value={rt}>
                  {routeLabel(rt)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="rounded-md border border-efs-border">
        <Table>
          <TableHeader>
            <TableRow>
              {canBulkCreate ? (
                <TableHead className="w-8">
                  <input
                    type="checkbox"
                    aria-label={t('efs.operations.dismantling.bulk_create.select_all', {
                      defaultValue: 'このページを全選択',
                    })}
                    checked={allSelectableSelected}
                    disabled={selectableRows.length === 0}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedIds(
                          new Set(selectableRows.map((pkg) => pkg.inbound_package_id)),
                        );
                      } else {
                        setSelectedIds(new Set());
                      }
                    }}
                  />
                </TableHead>
              ) : null}
              <TableHead>
                {t('efs.operations.dismantling.list.package_code', { defaultValue: '包装コード' })}
              </TableHead>
              <TableHead>
                {t('efs.operations.dismantling.list.item_name', { defaultValue: '品名' })}
              </TableHead>
              <TableHead>
                {t('efs.operations.dismantling.list.warehouse', { defaultValue: '倉庫' })}
              </TableHead>
              <TableHead className="text-right">
                {t('efs.operations.dismantling.list.net_kg', { defaultValue: '正味 kg' })}
              </TableHead>
              <TableHead>
                {t('efs.operations.dismantling.list.route', { defaultValue: '路線' })}
              </TableHead>
              <TableHead>
                {t('efs.operations.dismantling.list.progress', { defaultValue: '拆解状態' })}
              </TableHead>
              <TableHead className="w-56 text-right">
                {t('efs.operations.dismantling.list.actions', { defaultValue: '操作' })}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {query.isLoading ? (
              <TableRow>
                <TableCell
                  colSpan={canBulkCreate ? 8 : 7}
                  className="py-6 text-center text-efs-text-muted"
                >
                  {t('efs.common.messages.loading', { ns: 'common' })}
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={canBulkCreate ? 8 : 7}
                  className="py-6 text-center text-efs-text-muted"
                >
                  {t('efs.common.messages.empty', { ns: 'common' })}
                </TableCell>
              </TableRow>
            ) : (
              rows.map((pkg) => {
                const progress = pkg.progress_percent;
                const hasSession = !!pkg.dismantling_session_id;
                const effectiveStatus: DismantlingOperationalStatus =
                  pkg.operational_status ??
                  (!hasSession
                    ? 'draft'
                    : pkg.session_status === 'locked'
                      ? 'locked'
                      : pkg.session_status === 'confirmed'
                        ? 'confirmed'
                        : pkg.output_count > 0 || progress != null
                          ? 'in_progress'
                          : 'draft');
                const showReturnToSorting = canReturnToSorting(pkg);
                const cancelLoading =
                  !!pkg.dismantling_session_id &&
                  pkg.dismantling_session_id === cancelSessionId &&
                  cancelSessionQuery.isFetching;
                const cancelLoadFailed =
                  !!pkg.dismantling_session_id &&
                  pkg.dismantling_session_id === cancelSessionId &&
                  !!cancelSessionQuery.error;
                const warehouse =
                  pkg.warehouse_code && pkg.warehouse_display_name
                    ? `${pkg.warehouse_code} — ${pkg.warehouse_display_name}`
                    : (pkg.warehouse_display_name ?? '—');
                return (
                  <TableRow key={pkg.inbound_package_id}>
                    {canBulkCreate ? (
                      <TableCell className="w-8">
                        <input
                          type="checkbox"
                          aria-label={pkg.package_code}
                          title={
                            pkg.dismantling_session_id
                              ? t('efs.operations.dismantling.bulk_create.already_created', {
                                  defaultValue: '既に解体作業が作成済みです',
                                })
                              : undefined
                          }
                          disabled={!!pkg.dismantling_session_id}
                          checked={selectedIds.has(pkg.inbound_package_id)}
                          onChange={() => togglePkgSelected(pkg)}
                        />
                      </TableCell>
                    ) : null}
                    <TableCell className="font-mono text-xs">{pkg.package_code}</TableCell>
                    <TableCell className="text-xs">{pkg.incoming_item_name}</TableCell>
                    <TableCell className="text-xs">{warehouse}</TableCell>
                    <TableCell className="text-right text-xs">{pkg.net_kg ?? '—'}</TableCell>
                    <TableCell className="text-xs">{routeLabel(pkg.route_type)}</TableCell>
                    <TableCell>
                      <Badge
                        variant={progressTone(effectiveStatus)}
                        className={
                          progress != null && progress > 100
                            ? 'border-red-300 text-red-900 dark:border-red-700 dark:text-red-100'
                            : undefined
                        }
                      >
                        {operationalStatusLabel(
                          pkg.operational_status,
                          pkg.session_status,
                          hasSession,
                          pkg.output_count,
                          progress,
                        )}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button size="sm" variant="outline" onClick={() => setActivePkgs([pkg])}>
                          <Hammer className="mr-1 h-4 w-4" />
                          {t('efs.operations.dismantling.actions.open_session', {
                            defaultValue: '拆解作業',
                          })}
                        </Button>
                        {showReturnToSorting ? (
                          <Button
                            size="sm"
                            variant="destructive"
                            title={
                              cancelLoading
                                ? t('efs.operations.dismantling.cancel.loading_session', {
                                    defaultValue: '取消対象を読み込んでいます…',
                                  })
                                : cancelLoadFailed
                                  ? t('efs.operations.dismantling.cancel.retry_session', {
                                      defaultValue:
                                        '取消対象の読み込みに失敗しました。再試行します。',
                                    })
                                  : undefined
                            }
                            disabled={cancelLoading}
                            onClick={() => {
                              if (!pkg.dismantling_session_id) return;
                              if (pkg.dismantling_session_id === cancelSessionId) {
                                void cancelSessionQuery.refetch();
                                return;
                              }
                              setCancelSessionId(pkg.dismantling_session_id);
                            }}
                          >
                            {cancelLoading ? (
                              <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                            ) : (
                              <RotateCcw className="mr-1 h-4 w-4" />
                            )}
                            {t('efs.operations.dismantling.cancel.action', {
                              defaultValue: '取消して仕分けへ戻す',
                            })}
                          </Button>
                        ) : null}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      <ListPagination
        page={page}
        perPage={perPage}
        total={total}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        onPageChange={(nextPage) => {
          setSelectedIds(new Set());
          setPage(nextPage);
        }}
        onPerPageChange={(nextPerPage) => {
          setSelectedIds(new Set());
          setPage(1);
          setPerPage(nextPerPage === 100 ? 100 : DEFAULT_PER_PAGE);
        }}
      />

      {activePkgs ? (
        <DismantlingSessionDialog
          key={activePkgs.map((pkg) => pkg.inbound_package_id).join(',')}
          batchId={batchId ?? ''}
          pkgs={activePkgs}
          candidatePkgs={rows}
          open={!!activePkgs}
          onOpenChange={(v) => {
            if (!v) {
              setActivePkgs(null);
              setSelectedIds(new Set());
              // The dropped-pallet notice outlives the dialog on purpose: the
              // modal hides it while open, so closing is when it gets read.
              if (
                searchParams.has('session') ||
                searchParams.has('package') ||
                searchParams.has('packages')
              ) {
                const next = new URLSearchParams(searchParams);
                next.delete('session');
                next.delete('package');
                next.delete('packages');
                setSearchParams(next, { replace: true });
              }
            }
          }}
        />
      ) : null}

      {batchId ? (
        <CancelledDismantlingSessionsDialog
          batchId={batchId}
          open={cancelledOpen}
          onOpenChange={setCancelledOpen}
        />
      ) : null}

      <CancelDismantlingSessionDialog
        open={!!cancelSessionId && !!cancelSessionQuery.data}
        onOpenChange={(open) => {
          if (!open) setCancelSessionId(null);
        }}
        session={cancelSessionQuery.data}
        showTechnicalDetails={hasPermission(user, 'system.admin')}
        onCancelled={() => setCancelSessionId(null)}
      />

      {/* Sticky bottom action toolbar — same FAB pattern as SortingListPage. */}
      {canBulkCreate && selectedIds.size > 0 ? (
        <div className="pointer-events-none fixed inset-x-0 bottom-0 z-40 flex justify-center px-4 pb-4">
          <div className="pointer-events-auto flex flex-wrap items-center gap-2 rounded-full border border-amber-300 bg-amber-50/95 px-4 py-2 text-sm shadow-lg backdrop-blur dark:border-amber-700 dark:bg-amber-950">
            <span className="font-semibold text-amber-900 dark:text-amber-100">
              {t('efs.operations.dismantling.bulk_create.selected', {
                count: selectedRows.length,
                defaultValue: '{{count}}件選択中',
              })}
            </span>
            <span className="text-xs text-amber-900 dark:text-amber-100">
              {t('efs.operations.dismantling.bulk_create.selected_kg', {
                kg: formatKg(selectedKg, i18n.language),
                defaultValue: '対象重量 {{kg}} kg',
              })}
            </span>
            <Button size="sm" onClick={openMergeDialog}>
              <Hammer className="mr-1 h-4 w-4" />
              {t('efs.operations.dismantling.bulk_create.action', {
                defaultValue: '選択分をまとめて解体登録',
              })}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setSelectedIds(new Set())}>
              {t('efs.common.actions.cancel', { ns: 'common', defaultValue: 'キャンセル' })}
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default DismantlingListPage;
